import { isBefore, startOfDay } from "date-fns";
import { memo, useCallback, useEffect, useRef } from "react";
import { DayItem } from "./day-item";

interface DaySelectorProps {
  weekDays: { day: string; date?: Date; dateKey: string; isAllTickedOff?: boolean }[];
  selectedDateKey: string;
  onSelectDate: (day: string, dateKey: string) => void;
  /**
   * If true, disables days before today
   */
  disablePastDays?: boolean;
  /**
   * `"default"` keeps the legacy purple selected state; `"v2"` uses the
   * near-black selected state from the My Plan redesign.
   */
  theme?: "default" | "v2";
}

export const DaySelector = memo(function DaySelector({
  weekDays,
  selectedDateKey,
  onSelectDate,
  disablePastDays = false,
  theme = "default",
}: DaySelectorProps) {
  const today = startOfDay(new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  const ensureSelectedDayVisible = useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      if (!selectedDateKey) {
        return;
      }

      const container = containerRef.current;
      if (!container) {
        return;
      }

      const selectedElement = Array.from(
        container.querySelectorAll<HTMLButtonElement>("[data-date-key]"),
      ).find((element) => element.dataset.dateKey === selectedDateKey);

      if (!selectedElement) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const selectedRect = selectedElement.getBoundingClientRect();
      const isVisible =
        selectedRect.left >= containerRect.left && selectedRect.right <= containerRect.right;

      if (!isVisible) {
        selectedElement.scrollIntoView({
          behavior,
          block: "nearest",
          inline: "center",
        });
      }
    },
    [selectedDateKey],
  );

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      ensureSelectedDayVisible();
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [selectedDateKey, weekDays, ensureSelectedDayVisible]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(() => {
      ensureSelectedDayVisible("auto");
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [ensureSelectedDayVisible]);

  return (
    <div ref={containerRef} className="no-scrollbar flex w-full gap-0 overflow-x-auto">
      {weekDays.map((day) => {
        const isSelected = selectedDateKey === day.dateKey;
        const isChecked = day.isAllTickedOff ?? false;
        const isPastDay =
          disablePastDays && day.date ? isBefore(startOfDay(day.date), today) : false;

        return (
          <DayItem
            key={day.dateKey}
            day={day}
            dateKey={day.dateKey}
            isSelected={isSelected}
            isChecked={isChecked}
            isDisabled={isPastDay}
            onSelectDate={onSelectDate}
            theme={theme}
          />
        );
      })}
    </div>
  );
});
